import type { LineItemApiResponse } from 'shared/src/api/line-item-api-types';
import type {
  PacingIntent,
  UpdateLineItemPatch,
  UpdateLineItemsToolInput
} from 'shared/src/api/ai-line-item-chat-types';
import {
  calcEffectiveTotalPaced,
  getInapplicableFields,
  mergePatchedDates,
  resolveLineItemPacing,
  type ResolvedPacing
} from 'shared/src/domain/line-item/pacing-patch';
import {
  validateLineItemPacing,
  type ValidationIssue
} from 'shared/src/domain/line-item/pacing-validator';
import { isAfter } from 'shared/src/date-string-utils/date-utils';

// Code-free issue shape for the card — it renders messages, not IssueCodes, and the synthetic
// "missing line item" / "inapplicable field" errors have no pacing-validator code.
export type RowIssue = {
  path: string;
  severity: 'error' | 'warning';
  message: string;
};

export type FieldChange = {
  field: string;
  before: string;
  after: string;
  // On a promoted item this field routes to the LIVE execution line item (no promote gate).
  live: boolean;
};

export type BatchUpdateRow = {
  lineItemId: string;
  name: string;
  missing: boolean; // line item not found in the current proposal rows
  // before → after for every patched field (pacing rendered separately)
  fieldChanges: FieldChange[];
  resolvedPacing: ResolvedPacing | null;
  errors: RowIssue[]; // blocking — Confirm is disabled while any row has one
  warnings: RowIssue[]; // advisory — never block Confirm
  // offered when a blocking pacing error exists and the patch has no explicit pacing intent
  canOfferMonthlyFix: boolean;
  // Any field on this row routes to live execution — escalates the card to the live tier.
  routesToExecution: boolean;
};

// An edit's patch with any one-click pacing fix layered on top. The card MUST send exactly what
// the preview showed, so both buildBatchUpdateRows and the confirm mutation go through this.
export function applyPacingOverride(
  edit: UpdateLineItemsToolInput['edits'][number],
  pacingOverrides: Record<string, PacingIntent | undefined>
): UpdateLineItemPatch {
  return { ...edit.patch, pacing: pacingOverrides[edit.line_item_id] ?? edit.patch.pacing };
}

/**
 * Pure per-row derivation for the batch update card: resolves each edit's pacing consequence via
 * the shared resolver and runs the same isomorphic validator the backend and the line-item form
 * run, so what the user sees here is what would be written. `pacingOverrides` layers a one-click
 * deterministic fix (e.g. "Regenerate monthly blocks") on top of the model's patch without
 * mutating the original tool input.
 */
export function buildBatchUpdateRows(
  edits: UpdateLineItemsToolInput['edits'],
  currentById: Record<string, LineItemApiResponse>,
  pacingOverrides: Record<string, PacingIntent | undefined>
): BatchUpdateRow[] {
  return edits.map(edit => {
    const current = currentById[edit.line_item_id];
    if (!current) {
      return blockedRow(edit.line_item_id, edit.line_item_name ?? edit.line_item_id, {
        missing: true,
        message: 'Line item not found in this proposal'
      });
    }
    // The pacing resolver throws for zero_margin and (via generateMonthlyBlocks) for an inverted
    // date range — guard before calling it and surface a blocking row error instead.
    if (current.line_item_type === 'zero_margin') {
      return blockedRow(edit.line_item_id, current.name, {
        message: 'Zero-margin line items cannot be edited by the assistant'
      });
    }

    const patch = applyPacingOverride(edit, pacingOverrides);
    const mergedDates = mergePatchedDates(current, patch);
    if (isAfter(mergedDates.from, mergedDates.to)) {
      return blockedRow(edit.line_item_id, current.name, {
        message: `Start date ${mergedDates.from} is after end date ${mergedDates.to}`
      });
    }
    const promoted = current.execution_status !== 'no_execution';
    const inapplicable = getInapplicableFields(current.line_item_type, patch);
    const resolvedPacing = resolveLineItemPacing(current, patch, promoted);

    // Same isomorphic validator the form runs on every render (selectors.ts) and the backend runs
    // before writing. Spend is backend-only (Cube.js), so rules 9-10 (billable revenue floors)
    // won't fire here — advisory only.
    const { issues } = validateLineItemPacing(
      {
        execution_status: current.execution_status,
        dates: current.dates,
        pacing_type: current.pacing_type,
        pacing_details: current.pacing_details
      },
      {
        dates: mergedDates,
        pacing_type: resolvedPacing?.pacing_type ?? current.pacing_type,
        pacing_details: resolvedPacing?.pacing_details ?? current.pacing_details,
        totalPaced: calcEffectiveTotalPaced(current, patch, promoted),
        priceFloor: current.line_item_type === 'standard' ? (patch.price ?? current.price) : null
      },
      []
    );

    const pacingErrors = issues.filter(i => i.severity === 'error');
    const pacingWarnings = issues.filter(i => i.severity === 'warning');

    const errors: RowIssue[] = [
      ...pacingErrors.map(toRowIssue),
      ...inapplicable.map(field => ({
        path: field,
        severity: 'error' as const,
        message: `${field} does not apply to a ${current.line_item_type} line item`
      }))
    ];

    const fieldChanges = diffPatchFields(current, patch, promoted);
    return {
      lineItemId: edit.line_item_id,
      name: current.name,
      missing: false,
      fieldChanges,
      resolvedPacing,
      errors,
      warnings: pacingWarnings.map(toRowIssue),
      canOfferMonthlyFix:
        canFixWithMonthlyBlocks(pacingErrors) &&
        patch.pacing === undefined &&
        current.pacing_type === 'custom',
      routesToExecution: fieldChanges.some(c => c.live)
    };
  });
}

function canFixWithMonthlyBlocks(pacingErrors: ValidationIssue[]): boolean {
  return pacingErrors.some(i => i.code === 'block_outside_flight' || i.code === 'totals_mismatch');
}

function toRowIssue(issue: ValidationIssue): RowIssue {
  return { path: issue.path, severity: issue.severity, message: issue.message };
}

// A row that can't be previewed at all — one blocking error, nothing else to show.
function blockedRow(
  lineItemId: string,
  name: string,
  opts: { missing?: boolean; message: string }
): BatchUpdateRow {
  return {
    lineItemId,
    name,
    missing: opts.missing ?? false,
    fieldChanges: [],
    resolvedPacing: null,
    errors: [{ path: 'line_item_id', severity: 'error', message: opts.message }],
    warnings: [],
    canOfferMonthlyFix: false,
    routesToExecution: false
  };
}

// before → after strings for each defined non-pacing patch key, reading the "before" off the
// current row (dates via current.dates.from/to; arrays joined; null/undefined → '—'). On a
// promoted item the locked fields route to LIVE execution — marked so the card can badge them.
export function diffPatchFields(
  current: LineItemApiResponse,
  patch: UpdateLineItemPatch,
  promoted: boolean
): FieldChange[] {
  const changes: FieldChange[] = [];
  const push = (field: string, before: unknown, after: unknown, live = false) => {
    changes.push({ field, before: formatFieldValue(before), after: formatFieldValue(after), live });
  };

  if (patch.name !== undefined) push('name', current.name, patch.name);
  if (patch.price !== undefined) push('price', current.price, patch.price);
  if (patch.geo !== undefined) push('geo', current.geo, patch.geo);
  if (patch.date_from !== undefined) push('date_from', current.dates.from, patch.date_from);
  if (patch.date_to !== undefined) push('date_to', current.dates.to, patch.date_to);
  if (patch.targeting !== undefined) push('targeting', current.targeting, patch.targeting);
  if (patch.audience !== undefined) push('audience', current.audience, patch.audience);
  if (patch.ad_formats !== undefined) push('ad_formats', current.ad_formats, patch.ad_formats);
  if (patch.notes !== undefined) push('notes', current.notes, patch.notes);
  if (patch.planned_unit_cost !== undefined) {
    push('planned_unit_cost', current.planned_unit_cost, patch.planned_unit_cost, promoted);
  }
  if (patch.media_platform_ids !== undefined) {
    push('media_platform_ids', current.media_platform_ids, patch.media_platform_ids, promoted);
  }
  if (patch.purchase_order_number !== undefined) {
    push('purchase_order_number', current.purchase_order_number, patch.purchase_order_number);
  }
  if (patch.unit_price !== undefined) push('unit_price', current.unit_price, patch.unit_price);
  if (patch.media_budget !== undefined) {
    push('media_budget', current.media_budget, patch.media_budget);
  }
  if (patch.estimated_units !== undefined) {
    push('estimated_units', current.estimated_units, patch.estimated_units);
  }

  return changes;
}

function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '—';
  return String(value);
}

export function hasBlockingIssues(rows: BatchUpdateRow[]): boolean {
  return rows.some(r => r.errors.length > 0);
}
